import * as pdfjsLib from 'pdfjs-dist';

export type ImageFormat = 'png' | 'jpeg';

export interface PageImageResult {
  pageNumber: number; // 1-based
  blob: Blob;
}

export interface ImageExportOptions {
  scale: number;
  format: ImageFormat;
  quality?: number; // 0-1, JPEG only
}

/** Render a single 1-based PDF page to a raster image blob. */
export const renderPdfPageToImage = async (
  file: File,
  pageNumber: number,
  opts: ImageExportOptions
): Promise<Blob> => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale: opts.scale });

  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not create a canvas context for rendering.');
  }

  if (opts.format === 'jpeg') {
    // JPEG has no alpha channel — fill white first so transparent PDF
    // backgrounds don't render as black.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  await page.render({ canvasContext: ctx, viewport }).promise;

  const mime = opts.format === 'jpeg' ? 'image/jpeg' : 'image/png';
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode image.'))),
      mime,
      opts.quality
    );
  });
};

/** Render multiple 1-based PDF pages to image blobs, sequentially. */
export const renderPdfPagesToImages = async (
  file: File,
  pageNumbers: number[],
  opts: ImageExportOptions,
  onProgress?: (done: number, total: number) => void
): Promise<PageImageResult[]> => {
  const results: PageImageResult[] = [];
  for (const pageNumber of pageNumbers) {
    const blob = await renderPdfPageToImage(file, pageNumber, opts);
    results.push({ pageNumber, blob });
    onProgress?.(results.length, pageNumbers.length);
  }
  return results;
};
